import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BaseGroupType } from 'src/AngularJS/Models/group-type.model';
import { GroupSelectorPostObject } from 'src/AngularJS/Reusables/group-selector.models';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AttributeSelectorService } from 'src/app/reusables/attribute-selector/attribute-selector.service';
import { DateRangeSelectorService } from 'src/app/reusables/date-range-selector/date-range-selector.service';
import { GroupSelectorService } from 'src/app/reusables/group-selector/group-selector.service';
import { DashboardManager } from 'src/shared/models/dashboard-manager';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerDateRangeInfo, ModuleSettingsView } from 'src/shared/models/module-manager/module-manager-settings.models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { DashboardService } from '../dashboard.service';
import { ModuleContainerService } from '../module-container/module-container.service';

@Component({
    selector: 'cv-dashboard-filter-slideout',
    templateUrl: './dashboard-filter-slideout.component.html',
    styleUrls: ['./dashboard-filter-slideout.component.scss'],
    animations: [
        trigger('visibility', [
            state('shown', style({
                display: 'flex',
                right: '0px',
            })),
            state('hidden', style({
                display: 'none',
                right: '-392px',
            })),
            transition('shown <=> hidden', animate('1s ease')),
        ]),
    ],
})
export class DashboardFilterSlideoutComponent implements OnInit, OnChanges {
    @Input() open = false;
    @Input() dashboardManager: DashboardManager;
    @Output() openChange = new EventEmitter<boolean>();

    get dashboardKey() {
        return this.dashboardManager?.dashboardKey;
    }

    intervalTypes = ['Half Hour', 'Hour', 'Day', 'Day of Week', 'Week', 'Month', 'Year', '15 Minute'];
    ready = false;
    hasOpened = false;
    dateRangeLoaded = false;
    availableDateRanges: ModuleManagerDateRangeInfo[];
    settingsManager: SlideoutSettingsManager;
    loadedManagers = new Map<string, SlideoutSettingsManager>();

    showToast = false;
    toastIsError = false;
    dateExpanded = false;
    intervalExpanded = false;
    groupsExpanded = false;
    attrExpanded = false;
    inUserCatView = false;
    inDataCatView = false;

    isApplying = false;
    applyingDates = false;
    applyingInterval = false;
    applyingGroups = false;
    applyingAttrs = false;

    constructor(
        private attributeSelectorService: AttributeSelectorService,
        private dateRangeSelectorService: DateRangeSelectorService,
        private groupSelectorService: GroupSelectorService,
        private moduleContainerService: ModuleContainerService,
        private navigationService: NavigationService,
        private dashboardService: DashboardService,
    ) {
        this.loadDateRanges();
    }

    ngOnInit() {

    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.open)
            this.onOpenChanged();

        if (changes.dashboardManager)
            this.onDashboardChanged();
    }

    async loadDateRanges() {
        const dateRanges = await this.dashboardService.getAvailableDateRange();
        this.availableDateRanges = dateRanges.map(d => ModuleManagerDateRangeInfo.fromObject(d));
        this.dateRangeLoaded = true;
    }

    onOpenChanged() {
        if (this.open) {
            this.hasOpened = true;
            requestAnimationFrame(() => {
                this.ready = true;
                this.navigationService.showOverlay = true;
            });
        } else {
            this.navigationService.showOverlay = false;
        }
    }

    async onDashboardChanged() {
        if (this.dashboardManager === null) {
            this.settingsManager = null;
            return;
        }

        const key = this.dashboardManager.dashboardKey;
        if (!this.loadedManagers.has(key)) {
            const manager = new SlideoutSettingsManager(this.attributeSelectorService);
            let filterStateString: string;
            try {
                filterStateString = await this.dashboardService.getDashboardFilterSettings(key);
            } catch {
                filterStateString = '';
            }

            if (!filterStateString || !manager.loadFromState(filterStateString))
                manager.setDefaultSettings();

            this.loadedManagers.set(key, manager);
        }

        this.settingsManager = this.loadedManagers.get(key);
        this.settingsManager.availableDateRanges = this.availableDateRanges;
    }

    async applyDateTime() {
        this.isApplying = true;
        this.applyingDates = true;

        this.settingsManager.beforeSettingsApplied.next();
        await Promise.all(this.settingsManager.waitBeforeApplying);

        const moduleKeys = this.dashboardManager.getModuleKeys();
        const promises: PromiseLike<any>[] = [];
        for (const key of moduleKeys) {
            const moduleManager = this.dashboardManager.getModuleManager(key);
            const saveModulePromise = this.saveDateTime(moduleManager);
            promises.push(saveModulePromise);
        }

        await Promise.all(promises);
        await this.dashboardService.setDashboardFilterSettings(this.dashboardKey, this.settingsManager);

        this.showToast = true;
        this.isApplying = false;
        this.applyingDates = false;
    }

    async saveDateTime(moduleManager: ModuleManager) {
        const pageName = `${moduleManager.getModuleKey()}_temp`;
        let hasChanges = false;
        const selectedDateRange = ModuleManagerDateRangeInfo.fromObject(this.settingsManager.selectedDateRange);
        const isCustomDate = selectedDateRange.name.includes(' - ');
        // Reporting Set and Coaching Effectiveness have limited available date ranges
        const availableDateRanges = moduleManager.container?.availableDateRanges.map(d => d.name);
        const isValidDate = availableDateRanges.includes(selectedDateRange.name) ||
            (isCustomDate && availableDateRanges.includes('Custom...'));

        if (moduleManager.moduleSettings.selectedDateRange !== selectedDateRange
            && (!availableDateRanges || isValidDate)) {
            moduleManager.moduleSettings.selectedDateRange = selectedDateRange;
            await this.dateRangeSelectorService.saveDateRange(pageName, selectedDateRange.name);
            hasChanges = true;
        }

        if (moduleManager.moduleSettings.selectedDaysOfWeek !== this.settingsManager.selectedDaysOfWeek){
            moduleManager.moduleSettings.selectedDaysOfWeek = this.settingsManager.selectedDaysOfWeek;
            await this.dateRangeSelectorService.saveDaysOfWeek(pageName, this.settingsManager.selectedDaysOfWeek);
            hasChanges = true;
        }

        if (
            moduleManager.moduleSettings.isTimeSelectorEnabled !== this.settingsManager.isTimeSelectorEnabled ||
            moduleManager.moduleSettings.minTime !== this.settingsManager.minTime ||
            moduleManager.moduleSettings.maxTime !== this.settingsManager.maxTime
        ) {
            moduleManager.moduleSettings.isTimeSelectorEnabled = this.settingsManager.isTimeSelectorEnabled;
            moduleManager.moduleSettings.minTime = this.settingsManager.minTime;
            moduleManager.moduleSettings.maxTime = this.settingsManager.maxTime;
            await this.dateRangeSelectorService.saveTimeSettings(
                pageName,
                this.settingsManager.isTimeSelectorEnabled,
                this.settingsManager.minTime * 60,
                this.settingsManager.maxTime * 60,
            );
            hasChanges = true;
        }

        if (moduleManager.moduleSettings.invertTimeSelector !== this.settingsManager.invertTimeSelector){
            await this.dateRangeSelectorService.saveInvertTimeSelectorSetting(pageName, this.settingsManager.invertTimeSelector);
            hasChanges = true;
        }

        if (hasChanges) {
            await this.applyFilterOnModule(moduleManager);
        }
    }

    async applyInterval() {
        this.isApplying = true;
        this.applyingInterval = true;

        this.settingsManager.beforeSettingsApplied.next();
        await Promise.all(this.settingsManager.waitBeforeApplying);

        const moduleKeys = this.dashboardManager.getModuleKeys();
        const promises: PromiseLike<any>[] = [];
        for (const key of moduleKeys) {
            const moduleManager = this.dashboardManager.getModuleManager(key);
            if (!moduleManager?.moduleSettings?.intervalAmt)
                continue;
            promises.push(this.saveInterval(moduleManager));
        }

        await Promise.all(promises);
        await this.dashboardService.setDashboardFilterSettings(this.dashboardKey, this.settingsManager);

        this.showToast = true;
        this.isApplying = false;
        this.applyingInterval = false;
    }

    async saveInterval(moduleManager: ModuleManager) {
        const pageName = `${moduleManager.getModuleKey()}_temp`;
        if (moduleManager.moduleSettings.intervalAmt !== this.settingsManager.intervalAmt ||
            moduleManager.moduleSettings.intervaltype !== this.settingsManager.intervalType
        ) {
            await this.dateRangeSelectorService.saveIntervalSettings(
                pageName,
                this.settingsManager.intervalAmt,
                this.settingsManager.intervalType
            );
            await this.applyFilterOnModule(moduleManager);
        }
    }

    async applyGroups() {
        this.isApplying = true;
        this.applyingGroups = true;

        this.settingsManager.beforeSettingsApplied.next();
        await Promise.all(this.settingsManager.waitBeforeApplying);

        const moduleKeys = this.dashboardManager.getModuleKeys();
        const promises: PromiseLike<any>[] = [];
        for (const key of moduleKeys) {
            const moduleManager = this.dashboardManager.getModuleManager(key);
            if (!moduleManager?.moduleSettings?.empSelectorDTO && !moduleManager?.moduleSettings?.recSelectorDTO)
                continue;
            promises.push(this.saveGroups(moduleManager));
        }

        await Promise.all(promises);
        await this.dashboardService.setDashboardFilterSettings(this.dashboardKey, this.settingsManager);

        this.showToast = true;
        this.isApplying = false;
        this.applyingGroups = false;
    }

    async saveGroups(moduleManager: ModuleManager) {
        const pageName = `${moduleManager.getModuleKey()}_temp`;
        const showSelectedToggle = moduleManager.getModuleInfo().DTO.GroupSelectorShowToggle;
        moduleManager.moduleSettings.empSelectorDTO = this.settingsManager.empSelectorDTO;
        moduleManager.moduleSettings.recSelectorDTO = this.settingsManager.recSelectorDTO;
        moduleManager.moduleSettings.selectedGroupType = this.settingsManager.selectedToggle;

        const moduleSettingsManager = moduleManager.slideoutSettingsManager.getValue();
        if (moduleSettingsManager) {
            moduleSettingsManager.selectedEmpCount = this.settingsManager.getSelectedCount(BaseGroupType.Employee);
            moduleSettingsManager.selectedRecCount = this.settingsManager.getSelectedCount(BaseGroupType.Recording);
            moduleSettingsManager.selectedLevelName = this.settingsManager.selectedLevelName;
            moduleSettingsManager.empFilterLevelName = this.settingsManager.empFilterLevelName;
            moduleSettingsManager.recFilterLevelName = this.settingsManager.recFilterLevelName;
            moduleSettingsManager.empSelectorDTO = this.settingsManager.empSelectorDTO;
            moduleSettingsManager.recSelectorDTO = this.settingsManager.recSelectorDTO;
        }

        const promises: PromiseLike<any>[] = [];

        if (moduleManager.moduleSettings.empSelectorDTO) {
            const empSaveObject: GroupSelectorPostObject = {
                selectedGroup1Ids: this.settingsManager.empSelectorDTO._SelectedGroup1IDList,
                selectedGroup2Ids: this.settingsManager.empSelectorDTO._SelectedGroup2IDList,
                selectedGroup3Ids: this.settingsManager.empSelectorDTO._SelectedGroup3IDList,
                selectedGroup4Ids: this.settingsManager.empSelectorDTO._SelectedGroup4IDList,
                selectedGroup5Ids: this.settingsManager.empSelectorDTO._SelectedGroup5IDList,
                toggleGroupData: this.settingsManager.empSelectorDTO.toggleGroupData,
            };

            if (moduleManager.getModuleInfo().DTO.GroupSelectorShowToggle) {
                if (this.settingsManager.selectedToggle === BaseGroupType.Employee) {
                    moduleManager.moduleSettings.selectedGrouplevel = this.settingsManager.empSelectorDTO.SelectedGroupLevel;
                    empSaveObject.selectedGroupLevel = this.settingsManager.empSelectorDTO.SelectedGroupLevel;
                    empSaveObject.selectedToggle = true;
                } else {
                    empSaveObject.selectedGroupLevel = this.settingsManager.empSelectorDTO.SelectedMinLevel;
                    empSaveObject.selectedToggle = false;
                }
            }

            promises.push(this.groupSelectorService.saveGroupSelector(pageName, BaseGroupType.Employee, empSaveObject));
        }

        if (moduleManager.moduleSettings.recSelectorDTO) {
            const recSaveObject: GroupSelectorPostObject = {
                selectedGroup1Ids: this.settingsManager.recSelectorDTO._SelectedGroup1IDList,
                selectedGroup2Ids: this.settingsManager.recSelectorDTO._SelectedGroup2IDList,
                selectedGroup3Ids: this.settingsManager.recSelectorDTO._SelectedGroup3IDList,
                selectedGroup4Ids: this.settingsManager.recSelectorDTO._SelectedGroup4IDList,
                selectedGroup5Ids: this.settingsManager.recSelectorDTO._SelectedGroup5IDList,
                toggleGroupData: this.settingsManager.recSelectorDTO.toggleGroupData,
            };

            if (moduleManager.getModuleInfo().DTO.GroupSelectorShowToggle) {
                if (this.settingsManager.selectedToggle === BaseGroupType.Recording) {
                    moduleManager.moduleSettings.selectedGrouplevel = this.settingsManager.recSelectorDTO.SelectedGroupLevel;
                    recSaveObject.selectedGroupLevel = this.settingsManager.recSelectorDTO.SelectedGroupLevel;
                    recSaveObject.selectedToggle = true;
                } else {
                    recSaveObject.selectedGroupLevel = this.settingsManager.recSelectorDTO.SelectedMinLevel;
                    recSaveObject.selectedToggle = false;
                }
            }

            promises.push(this.groupSelectorService.saveGroupSelector(pageName, BaseGroupType.Recording, recSaveObject));
            window['DashboardSettings']?.ReloadMetrics(pageName, null,
                result => $(moduleManager.container.element).find('.metricSelectorContainer').html(
                    $(result).find(".metricSelectorContainer").html()
                ),
                err => window['showErrorMessage'](err.get_message())
            );
        }

        await Promise.all(promises);
        await this.applyFilterOnModule(moduleManager);
    }

    async applyAttributes() {
        this.isApplying = true;
        this.applyingAttrs = true;

        this.settingsManager.beforeSettingsApplied.next();
        await Promise.all(this.settingsManager.waitBeforeApplying);

        const moduleKeys = this.dashboardManager.getModuleKeys();
        const promises: PromiseLike<any>[] = [];
        for (const key of moduleKeys) {
            const moduleManager = this.dashboardManager.getModuleManager(key);
            promises.push(this.saveAttributes(moduleManager));
        }

        this.showToast = true;
        this.isApplying = false;
        this.applyingAttrs = false;
    }

    async saveAttributes(moduleManager: ModuleManager) {
        const pageName = `${moduleManager.getModuleKey()}_temp`;
        moduleManager.moduleSettings.dataAttributesModel = this.settingsManager.dataAttributesModel;
        moduleManager.moduleSettings.userAttributesModel = this.settingsManager.userAttributesModel;
        moduleManager.moduleSettings.selectedGroupType = this.settingsManager.selectedToggle;

        const moduleSettingsManager = moduleManager.slideoutSettingsManager.getValue();
        if (moduleSettingsManager) {
            moduleSettingsManager.selectedLevelName = this.settingsManager.selectedLevelName;
            moduleSettingsManager.onAttributeReloaded.next(this.settingsManager.dataAttributesModel);
            moduleSettingsManager.onAttributeReloaded.next(this.settingsManager.userAttributesModel);
        }

        await Promise.all([
            this.attributeSelectorService.saveSelectorModel(pageName, this.settingsManager.dataAttributesModel),
            this.attributeSelectorService.saveSelectorModel(pageName, this.settingsManager.userAttributesModel),
        ]);

        await this.applyFilterOnModule(moduleManager);
    }

    async applyFilterOnModule(moduleManager: ModuleManager) {
        const moduleContainer = moduleManager.container;
        const moduleKey = moduleManager.getModuleKey();
        moduleManager.setAutoRefreshReady(false);
        moduleContainer.genericSettingsData = null;
        moduleContainer.isFirstSettingsOpen = false;
        try {
            const response = await this.moduleContainerService.applyGenericSettings(moduleContainer.moduleId, moduleKey);
            if (response.status_code !== 200) {
                throw response;
            }

            // Update module's settings to match the updated generic settings on the server
            Object.assign(moduleManager.moduleSettings, response.data);

            // let settingsManager know that we are done applying settings
            this.settingsManager.onSettingsApplied.next();

            // update settings view
            moduleContainer.currentSettingsComponent?.setGenericSettingsControlSettings(moduleManager.moduleSettings);
            moduleManager.setAutoRefreshReady();
            moduleContainer.hasSettingsApplied = true;
            moduleManager.moduleOptions.onSettingsApplied(Object.assign({}, moduleManager.moduleSettings));
            moduleManager.saveState();
            if (moduleManager.settingsView === ModuleSettingsView.Modal) {
                await moduleManager.loadGenericSettings();
            }

            this.dashboardManager.requestPublishNeeded();
        } catch (e) {
            console.error(e);
            this.toastIsError = true;
            this.showToast = true;
        }
    }

    closeToast() {
        this.showToast = false;
        this.toastIsError = false;
    }

    close() {
        this.open = false;
        this.navigationService.showOverlay = false;
        this.openChange.emit(false);
    }
}
